import React, { useEffect } from 'react'; // Keep React import for clarity, though often implicit with JSX transform
import PropTypes from 'prop-types'; // Import PropTypes
import { useGetTokenCreator } from '../../hooks';
import UserScoreHeader from '../User/UserScores.component';

// --- MUI v5 Imports ---
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import { Chip, Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
// Optional: import Typography from '@mui/material/Typography'; // If needed for other text

/**
 * Component to display the creator of a token using UserScoreHeader.
 * Handles loading and error states using MUI components.
 */
const TokenCreator = ({ tokenId }) => {
    // Fetch token creator data using the custom hook
    const { data, loading, error } = useGetTokenCreator(tokenId);
    const [creator, setCreator] = React.useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        // If data is available, set the creator state
        if (data?.creator) {
            setCreator(data.creator);
        }
    }
        , [data, setCreator]);

    // --- Loading State ---
    if (loading) {
        // Display a centered MUI loading spinner
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 2 }}>
                <CircularProgress />
            </Box>
        );
    }

    // --- Error State ---
    if (error) {
        // Display an MUI Alert component for the error
        return (
            <Alert severity="error" sx={{ m: 2 }}> {/* Add margin for spacing */}
                Error fetching token creator: {error.message || 'An unknown error occurred.'}
            </Alert>
        );
        // Alternative using Typography:
        // return <Typography color="error" sx={{ p: 2 }}>Error: {error.message}</Typography>;
    }

    // --- Success State ---
    // Check if data exists and has the creator property
    if (data?.creator) {
        return <UserScoreHeader userId={data.creator} />;
        // Render the UserScoreHeader component with the creator ID
        return (
            <Stack >
                <Typography variant="h6" sx={{ mb: 1 }}>
                    Creator
                </Typography>

                <UserScoreHeader userId={data.creator} />;
            </Stack>
        );

    }

    // --- No Creator Found State ---
    // Handle the case where loading is false, error is null, but no creator is found
    // You might want to return null or a specific message/component
    return (
        <Box sx={{ p: 2, fontStyle: 'italic' }}> {/* Basic styling for the message */}
            Creator information not available for this token.
        </Box>
        // Or return null if you want nothing to render in this case:
        // return null;
    );
};

// Define prop types for the component
TokenCreator.propTypes = {
    tokenId: PropTypes.string.isRequired,
};

export default TokenCreator;